<template>
  <div>
    <v-container fluid class="pa-6">
      <!-- Header Section -->
      <div class="dashboard-header mb-8">
        <h1 class="dashboard-title">📊 สถิตินักเรียน/นักศึกษา ตามจังหวัด</h1>
        <p class="dashboard-subtitle">
          สำนักงานคณะกรรมการการอาชีพวศึกษา (สอศ.)
        </p>
      </div>

      <!-- Filter Section -->
      <v-card class="filter-card mb-8" elevation="2">
        <v-card-text class="pa-6">
          <div class="filter-grid">
            <!-- Year Filter -->
            <div class="filter-item">
              <label class="filter-label">ปีการศึกษา</label>
              <v-autocomplete
                v-model="filters.rate_work_college_year"
                :items="uniqueYears"
                clearable
                outlined
                dense
                class="filter-input"
                @change="applyFilters"
              />
            </div>

            <!-- Province Filter -->
            <div class="filter-item">
              <label class="filter-label">จังหวัด</label>
              <v-autocomplete
                v-model="filters.province_name"
                :items="uniqueProvinces"
                clearable
                outlined
                dense
                class="filter-input"
                @change="applyFilters"
              />
            </div>

            <!-- Class Filter -->
            <div class="filter-item">
              <label class="filter-label">ชั้นปี</label>
              <v-autocomplete
                v-model="filters.rate_work_college_class"
                :items="uniqueClasses"
                clearable
                outlined
                dense
                class="filter-input"
                @change="applyFilters"
              />
            </div>

            <!-- Action Buttons -->
            <div class="filter-actions">
              <v-btn
                class="btn-reset"
                outlined
                color="secondary"
                small
                @click="resetFilters"
              >
                🔄 รีเซ็ต
              </v-btn>
              <v-btn
                class="btn-export"
                color="success"
                small
                @click="exportToExcel"
              >
                📊 ส่งออก Excel
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>


      <!-- Charts Section -->
      <div class="charts-grid mb-8">
        <!-- Chart by Province -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📍 สถิติตามจังหวัด (Top 15)</h3>
            </div>
            <div v-if="!isLoadingChart" class="chart-container">
              <div v-if="hasProvinceData" class="chart-wrapper">

                <apexchart
                  v-if="chartByProvinceSeries.length > 0"
                  type="bar"
                  :options="chartByProvinceOptions"
                  :series="chartByProvinceSeries"
                  height="420"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล กรุณาตรวจสอบการเชื่อมต่อ API</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>

        <!-- Chart by Year -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📅 สถิติตามปีการศึกษา</h3>
            </div>
            <div v-if="!isLoadingChart" class="chart-container">
              <div v-if="hasYearData" class="chart-wrapper">
                <apexchart
                  v-if="chartByYearSeries.length > 0"
                  type="line"
                  :options="chartByYearOptions"
                  :series="chartByYearSeries"
                  height="420"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล กรุณาตรวจสอบการเชื่อมต่อ API</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Second Row Charts -->
      <div class="charts-grid mb-8">
        <!-- Chart by Class -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">🎓 สถิติตามชั้นปี</h3>
            </div>
            <div v-if="!isLoadingChart" class="chart-container">
              <div v-if="hasClassData" class="chart-wrapper">
                <apexchart
                  v-if="chartByClassSeries.length > 0"
                  type="donut"
                  :options="chartByClassOptions"
                  :series="chartByClassSeries"
                  height="420"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล กรุณาตรวจสอบการเชื่อมต่อ API</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>

        <!-- Chart by Year and Class -->
        <v-card class="chart-card" elevation="2">
          <v-card-text class="pa-6">
            <div class="chart-header">
              <h3 class="chart-title">📊 สถิติตามปีและชั้นปี</h3>
            </div>
            <div v-if="!isLoadingChart" class="chart-container">
              <div v-if="hasYearClassData" class="chart-wrapper">
                <apexchart
                  v-if="chartByYearClassSeries.length > 0"
                  type="bar"
                  :options="chartByYearClassOptions"
                  :series="chartByYearClassSeries"
                  height="420"
                />
              </div>
              <div v-else class="empty-state">
                <p>ไม่มีข้อมูล กรุณาตรวจสอบการเชื่อมต่อ API</p>
              </div>
            </div>
            <div v-else class="loading-state">
              <v-progress-linear indeterminate />
            </div>
          </v-card-text>
        </v-card>
      </div>

      <!-- Detail Table -->
      <v-card class="table-card" elevation="2">
        <v-card-text class="pa-6">
          <div class="table-header mb-4">
            <h3 class="table-title">📋 รายละเอียดข้อมูล</h3>
            <span class="table-info">
              จำนวนระเบียน: {{ filteredData.length }}
            </span>
          </div>

          <v-data-table
            v-if="filteredData.length > 0"
            :headers="tableHeaders"
            :items="filteredData"
            :items-per-page="10"
            class="data-table"
            elevation="0"
          >
            <template v-slot:item.countstd="{ item }">
              <span class="count-badge">
                {{ formatNumber(parseInt(item.countstd || 0)) }}
              </span>
            </template>
          </v-data-table>

          <div v-else class="empty-table-state">
            <p>ไม่มีข้อมูล</p>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import * as XLSX from "xlsx";

export default {
  name: "StudentOverallCountProvince",
  data() {
    return {
      ApiKey: "HRvec2021",
      rawData: [],
      filteredData: [],
      isLoadingChart: true,
      filters: {
        rate_work_college_year: null,
        province_name: null,
        rate_work_college_class: null
      },
      tableHeaders: [
        { text: "ลำดับ", value: "index", width: "60px" },
        { text: "ปีการศึกษา", value: "rate_work_college_year", width: "120px" },
        { text: "จังหวัด", value: "province_name" },
        { text: "ชั้นปี", value: "rate_work_college_class", width: "100px" },
        {
          text: "จำนวนนักเรียน/นักศึกษา",
          value: "countstd",
          width: "150px",
          align: "right"
        }
      ]
    };
  },
  computed: {
    uniqueYears() {
      return [...new Set(this.rawData.map(item => item.rate_work_college_year))]
        .filter(y => y)
        .sort((a, b) => b - a);
    },
    uniqueProvinces() {
      return [...new Set(this.rawData.map(item => item.province_name))]
        .filter(p => p)
        .sort();
    },
    uniqueClasses() {
      return [...new Set(this.rawData.map(item => item.rate_work_college_class))]
        .filter(c => c)
        .sort();
    },
    totalStudents() {
      return this.filteredData.reduce(
        (sum, item) => sum + parseInt(item.countstd || 0),
        0
      );
    },
    hasProvinceData() {
      return this.groupedByProvince && Object.keys(this.groupedByProvince).length > 0;
    },
    hasYearData() {
      return this.groupedByYear && Object.keys(this.groupedByYear).length > 0;
    },
    hasClassData() {
      return this.groupedByClass && Object.keys(this.groupedByClass).length > 0;
    },
    hasYearClassData() {
      return this.groupedByYearClass && Object.keys(this.groupedByYearClass).length > 0;
    },
    groupedByProvince() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const province = item.province_name || "ไม่ระบุ";
        if (!grouped[province]) {
          grouped[province] = 0;
        }
        grouped[province] += parseInt(item.countstd || 0);
      });
      return grouped;
    },
    groupedByYear() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const year = item.rate_work_college_year;
        if (!grouped[year]) {
          grouped[year] = 0;
        }
        grouped[year] += parseInt(item.countstd || 0);
      });
      return grouped;
    },
    groupedByClass() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const cls = item.rate_work_college_class || "ไม่ระบุ";
        if (!grouped[cls]) {
          grouped[cls] = 0;
        }
        grouped[cls] += parseInt(item.countstd || 0);
      });
      return grouped;
    },
    groupedByYearClass() {
      const grouped = {};
      this.filteredData.forEach(item => {
        const year = item.rate_work_college_year;
        const cls = item.rate_work_college_class || "ไม่ระบุ";
        const key = `${year}`;

        if (!grouped[key]) {
          grouped[key] = {};
        }
        if (!grouped[key][cls]) {
          grouped[key][cls] = 0;
        }
        grouped[key][cls] += parseInt(item.countstd || 0);
      });
      return grouped;
    },
    chartByProvinceSeries() {
      try {
        const provinces = Object.keys(this.groupedByProvince)
          .sort((a, b) => this.groupedByProvince[b] - this.groupedByProvince[a])
          .slice(0, 15);

        const data = provinces.map(p => this.groupedByProvince[p]);

        return data.length > 0
          ? [
              {
                name: "จำนวนนักเรียน/นักศึกษา",
                data: data
              }
            ]
          : [];
      } catch (error) {
        console.error("Error in chartByProvinceSeries:", error);
        return [];
      }
    },
    chartByProvinceOptions() {
      const provinces = Object.keys(this.groupedByProvince)
        .sort((a, b) => this.groupedByProvince[b] - this.groupedByProvince[a])
        .slice(0, 15);

      return {
        chart: {
          type: "bar",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#3B82F6"],
        plotOptions: {
          bar: {
            horizontal: true,
            columnWidth: "70%",
            borderRadius: 4,
            dataLabels: {
              position: "right"
            }
          }
        },
        dataLabels: {
          enabled: true,
          offsetX: 10,
          style: {
            fontSize: "12px",
            colors: ["#3B82F6"],
            fontWeight: 600
          },
          formatter: val => this.formatNumberShort(val)
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["#ffffff"]
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10
          }
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        xaxis: {
           categories: provinces,
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },       
      };
    },
    chartByYearSeries() {
      try {
        const years = Object.keys(this.groupedByYear).sort();
        const data = years.map(year => this.groupedByYear[year]);

        return data.length > 0
          ? [
              {
                name: "จำนวนนักเรียน/นักศึกษา",
                data: data
              }
            ]
          : [];
      } catch (error) {
        console.error("Error in chartByYearSeries:", error);
        return [];
      }
    },
    chartByYearOptions() {
      const years = Object.keys(this.groupedByYear).sort();

      return {
        chart: {
          type: "line",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#10B981"],
        stroke: {
          curve: "smooth",
          width: 3
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10
          }
        },
        markers: {
          size: 5,
          colors: ["#10B981"],
          strokeColors: "#ffffff",
          strokeWidth: 2,
          hover: {
            size: 7
          }
        },
        dataLabels: {
          enabled: false
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        xaxis: {
          categories: years,
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },
        yaxis: {
          title: {
            text: "จำนวน (คน)",
            style: {
              fontSize: "12px",
              fontWeight: 600
            }
          },
          labels: {
            formatter: val => this.formatNumberShort(val),
            style: {
              fontSize: "11px"
            }
          }
        }
      };
    },
    chartByClassSeries() {
      try {
        const classes = Object.keys(this.groupedByClass).sort();
        return classes.map(cls => this.groupedByClass[cls]);
      } catch (error) {
        console.error("Error in chartByClassSeries:", error);
        return [];
      }
    },
    chartByClassOptions() {
      const classes = Object.keys(this.groupedByClass).sort();

      return {
        chart: {
          type: "donut",
          fontFamily: "Roboto, sans-serif",
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#14B8A6", "#06B6D4"],
        plotOptions: {
          pie: {
            donut: {
              size: "65%",
              labels: {
                show: true,
                name: {
                  fontSize: "12px"
                },
                value: {
                  fontSize: "16px",
                  fontWeight: 600,
                  formatter: val => this.formatNumberShort(val)
                },
                total: {
                  show: true,
                  fontSize: "13px",
                  fontWeight: 600,
                  label: "รวม",
                  formatter: val => this.formatNumber(val)
                }
              }
            }
          }
        },
        labels: classes,
        legend: {
          position: "bottom",
          fontSize: "12px",
          fontFamily: "Roboto, sans-serif"
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        dataLabels: {
          enabled: true,
          formatter: val => `${val.toFixed(1)}%`,
          style: {
            fontSize: "11px",
            fontWeight: 600
          }
        }
      };
    },
    chartByYearClassSeries() {
      try {
        const years = Object.keys(this.groupedByYearClass).sort();
        const allClasses = [
          ...new Set(
            Object.values(this.groupedByYearClass).flatMap(y => Object.keys(y))
          )
        ].sort();

        return allClasses.map(cls => ({
          name: cls,
          data: years.map(year => this.groupedByYearClass[year][cls] || 0)
        }));
      } catch (error) {
        console.error("Error in chartByYearClassSeries:", error);
        return [];
      }
    },
    chartByYearClassOptions() {
      const years = Object.keys(this.groupedByYearClass).sort();

      return {
        chart: {
          type: "bar",
          fontFamily: "Roboto, sans-serif",
          stacked: false,
          toolbar: {
            show: true,
            tools: {
              download: true,
              selection: true,
              zoom: true,
              zoomin: true,
              zoomout: true,
              pan: true,
              reset: true
            }
          },
          animations: {
            enabled: true,
            speed: 800
          }
        },
        colors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"],
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: "60%",
            borderRadius: 4
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["#ffffff"]
        },
        grid: {
          borderColor: "#e5e7eb",
          strokeDashArray: 3,
          padding: {
            top: 10,
            right: 10,
            bottom: 0,
            left: 10
          }
        },
        tooltip: {
          theme: "light",
          y: {
            formatter: val => `${this.formatNumber(val)} คน`
          }
        },
        xaxis: {
          categories: years,
          labels: {
            style: {
              fontSize: "12px"
            }
          }
        },
        yaxis: {
          title: {
            text: "จำนวน (คน)",
            style: {
              fontSize: "12px",
              fontWeight: 600
            }
          },
          labels: {
            formatter: val => this.formatNumberShort(val),
            style: {
              fontSize: "11px"
            }
          }
        },
        legend: {
          position: "top",
          fontSize: "12px",
          fontFamily: "Roboto, sans-serif"
        }
      };
    }
  },
  watch: {
    filteredData: {
      handler() {
        this.$forceUpdate();
      },
      deep: true
    }
  },
  async mounted() {
    await this.fetchStudentData();
  },
  methods: {
    formatNumber(num) {
      if (!num) return "0";
      return parseInt(num).toLocaleString("th-TH");
    },
    formatNumberShort(num) {
      if (!num) return "0";
      const n = parseInt(num);
      if (n >= 1000000) {
        return (n / 1000000).toFixed(1) + "M";
      } else if (n >= 1000) {
        return (n / 1000).toFixed(1) + "K";
      }
      return n.toLocaleString("th-TH");
    },
    async fetchStudentData() {
      try {
        this.isLoadingChart = true;

        if (!this.$http) {
          console.error("HTTP client not configured");
          this.isLoadingChart = false;
          return;
        }

        const response = await this.$http.post(
          "vw_student_overall_count_province.php",
          {
            ApiKey: this.ApiKey
          }
        );

        if (response.data && response.data.status) {
          this.rawData = response.data.data || [];
          this.applyFilters();
        } else {
          console.error("API error:", response.data);
        }

        this.isLoadingChart = false;
      } catch (error) {
        console.error("Error fetching student data:", error);
        this.isLoadingChart = false;
      }
    },
    applyFilters() {
      this.filteredData = this.rawData
        .filter(item => {
          const yearMatch =
            !this.filters.rate_work_college_year ||
            item.rate_work_college_year === this.filters.rate_work_college_year;
          const provinceMatch =
            !this.filters.province_name ||
            item.province_name === this.filters.province_name;
          const classMatch =
            !this.filters.rate_work_college_class ||
            item.rate_work_college_class ===
              this.filters.rate_work_college_class;

          return yearMatch && provinceMatch && classMatch;
        })
        .map((item, index) => ({
          ...item,
          index: index + 1
        }));
    },
    resetFilters() {
      this.filters.rate_work_college_year = null;
      this.filters.province_name = null;
      this.filters.rate_work_college_class = null;
      this.applyFilters();
    },
    exportToExcel() {
      try {
        const exportData = this.filteredData.map(item => ({
          "ลำดับ": item.index,
          "ปีการศึกษา": item.rate_work_college_year,
          "จังหวัด": item.province_name,
          "ชั้นปี": item.rate_work_college_class,
          "จำนวนนักเรียน/นักศึกษา": parseInt(item.countstd || 0)
        }));

        const ws = XLSX.utils.json_to_sheet(exportData, {
          header: [
            "ลำดับ",
            "ปีการศึกษา",
            "จังหวัด",
            "ชั้นปี",
            "จำนวนนักเรียน/นักศึกษา"
          ]
        });

        const wscols = [
          { wch: 8 },
          { wch: 15 },
          { wch: 20 },
          { wch: 12 },
          { wch: 25 }
        ];
        ws["!cols"] = wscols;

        // Create summary sheet
        const summaryData = [
          { "รายงาน": "สรุปข้อมูลสถิตินักเรียน/นักศึกษาตามจังหวัด" },
          { "รายงาน": "" },
          { "รายงาน": "สรุปข้อมูล", "ค่า": "" },
          { "รายงาน": "รวมนักเรียน/นักศึกษา", "ค่า": this.totalStudents },
          { "รายงาน": "จำนวนปีการศึกษา", "ค่า": this.uniqueYears.length },
          {
            "รายงาน": "จำนวนจังหวัด",
            "ค่า": this.uniqueProvinces.length
          },
          { "รายงาน": "จำนวนชั้นปี", "ค่า": this.uniqueClasses.length }
        ];

        const wsSummary = XLSX.utils.json_to_sheet(summaryData, {
          header: ["รายงาน", "ค่า"]
        });
        wsSummary["!cols"] = [{ wch: 30 }, { wch: 20 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, wsSummary, "สรุป");
        XLSX.utils.book_append_sheet(wb, ws, "รายละเอียด");

        const now = new Date();
        const dateStr = now.toISOString().slice(0, 10);
        const filename = `สถิตินักเรียนตามจังหวัด_${dateStr}.xlsx`;

        XLSX.writeFile(wb, filename);

        this.$notify({
          group: "foo",
          title: "สำเร็จ",
          text: "ส่งออกข้อมูล Excel สำเร็จ!",
          type: "success"
        });
      } catch (error) {
        console.error("Error exporting to Excel:", error);
        this.$notify({
          group: "foo",
          title: "ข้อผิดพลาด",
          text: "เกิดข้อผิดพลาดในการส่งออกข้อมูล",
          type: "error"
        });
      }
    }
  }
};
</script>

<style scoped>
.student-province-dashboard {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  min-height: 100vh;
  font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

/* Header Styles */
.dashboard-header {
  text-align: center;
  padding: 20px 0;
}

.dashboard-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
}

.dashboard-subtitle {
  font-size: 15px;
  color: #6b7280;
  font-weight: 500;
}

/* Filter Card */
.filter-card {
  background: white;
  border-radius: 12px;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  align-items: flex-end;
}

.filter-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.filter-input {
  border-radius: 6px;
}

.filter-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.btn-reset,
.btn-export {
  border-radius: 6px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.btn-reset {
  min-width: 120px;
}

.btn-export {
  min-width: 140px;
  background: #10b981 !important;
  color: white !important;
}

.btn-export:hover {
  background: #059669 !important;
}

/* Statistics Cards */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
}

.stat-card {
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border-left: 4px solid;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.stat-card-total {
  border-left-color: #3b82f6;
}

.stat-card-years {
  border-left-color: #10b981;
}

.stat-card-provinces {
  border-left-color: #f59e0b;
}

.stat-card-classes {
  border-left-color: #8b5cf6;
}

.stat-icon {
  font-size: 36px;
  min-width: 60px;
  text-align: center;
}

.stat-info {
  flex: 1;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
}

/* Charts Grid */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
  gap: 24px;
}

.chart-card {
  background: white;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.chart-card:hover {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.chart-header {
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 16px;
  margin-bottom: 20px;
}

.chart-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.chart-container {
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-wrapper {
  width: 100%;
}

.empty-state,
.empty-table-state {
  width: 100%;
  padding: 40px 20px;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
  background: #f9fafb;
  border-radius: 6px;
}

.loading-state {
  width: 100%;
  min-height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Table Card */
.table-card {
  background: white;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.table-card:hover {
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15) !important;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 16px;
}

.table-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.table-info {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}

.data-table {
  border-radius: 6px;
}

.data-table ::v-deep .v-data-table__wrapper {
  border-radius: 6px;
}

.data-table ::v-deep thead th {
  background: linear-gradient(180deg, #f3f4f6 0%, #e5e7eb 100%) !important;
  font-weight: 700 !important;
  color: #374151 !important;
  font-size: 13px !important;
}

.data-table ::v-deep tbody tr:hover {
  background: #f9fafb !important;
}

.data-table ::v-deep tbody td {
  font-size: 13px;
  color: #1f2937;
}

.count-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: 600;
  display: inline-block;
}

/* Responsive */
@media (max-width: 1200px) {
  .charts-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .dashboard-title {
    font-size: 24px;
  }

  .filter-grid {
    grid-template-columns: 1fr;
  }

  .filter-actions {
    flex-direction: column;
    width: 100%;
  }

  .btn-reset,
  .btn-export {
    width: 100%;
  }

  .stats-row {
    grid-template-columns: repeat(2, 1fr);
  }

  .stat-card {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .stat-icon {
    font-size: 28px;
  }

  .stat-value {
    font-size: 24px;
  }

  .charts-grid {
    grid-template-columns: 1fr;
  }

  .chart-container {
    min-height: 320px;
  }
}

@media (max-width: 480px) {
  .dashboard-title {
    font-size: 20px;
  }

  .stats-row {
    grid-template-columns: 1fr;
  }

  .stat-card {
    flex-direction: row;
    text-align: left;
  }

  .stat-icon {
    font-size: 24px;
    min-width: 50px;
  }

  .stat-value {
    font-size: 20px;
  }

  .data-table ::v-deep {
    font-size: 12px;
  }

  .data-table ::v-deep thead th,
  .data-table ::v-deep tbody td {
    padding: 8px !important;
  }
}
</style>